///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { GtthReportsService } from '../../../../services/project/reports.service';
import { GtthReportsTabs, REPORT_TABS } from '../reports-tabs.enum';
import { ExceedingMaxHopsDataDto, ExceedingMaxLinksDataDto, NodeConnectedDataDTO } from '../../../../generated/shared-types.model';
import { ExtendedNodeConnData } from './extended-node-conn-data.model';
import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { GtthProjectInfoViewService } from '../../../../layouts/navbar/project-info-view.service';
import { MatDialogRef } from '@angular/material/dialog';
import { PreloaderService } from '../../../../services/preloader.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-report-constraint-compliance-nodes-list',
    templateUrl: './report-constraint-compliance-nodes-list.component.html',
    styleUrls: ['../../project-windows.scss', './report-constraint-compliance-nodes-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthReportConstraintComplianceNodesListComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    public nodesList: ExtendedNodeConnData[];
    public collapseNodeList: boolean;
    public listType: string;
    public currentTab = GtthReportsTabs.ALL;
    public reportsTabs = GtthReportsTabs;
    public readonly tabsToShow = REPORT_TABS;
    public layers: Record<string, string>;
    public selectedNodes: string[];
    protected readonly close = close;
    private layersChangedSubscriber: Subscription;
    private nodesListOriginal: ExtendedNodeConnData[];

    constructor(
        public dialogRef: MatDialogRef<GtthReportConstraintComplianceNodesListComponent>,
        private readonly elementRef: ElementRef,
        private readonly historyService: GtthHistoryService,
        private readonly reportsService: GtthReportsService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly preloaderService: PreloaderService,
        private readonly projectInfoService: GtthProjectInfoViewService
    ) {
        super(elementRef);

        this.layersChangedSubscriber = projectInfoService.layerChangedSubscription().subscribe((value) => {
            if (this.nodesListOriginal) {
                this.filterNodesToShow(value);
            }
            this.getProjectLayers();
            this.changeDetector.markForCheck();
        });
    }

    ngOnInit() {
        this.preloaderService.show();

        this.collapseNodeList = true;

        this.getProjectLayers();

        if (this.listType === 'max-links') {
            this.reportsService.getExceedingMaxLinksNodes().subscribe((links: ExceedingMaxLinksDataDto[]) => {
                this.nodesListOriginal = links.map((value) => {
                    return { nodeid: value.nodeId } as ExtendedNodeConnData;
                });
                this.filterFrozenNodes();
                this.filterNodesToShow(this.projectInfoService.activeLayer);
                this.preloaderService.hide();
                this.changeDetector.markForCheck();
            });
        } else {
            this.reportsService.getExceedingMaxHopsNodes().subscribe((res: ExceedingMaxHopsDataDto[]) => {
                this.nodesListOriginal = res.map((value) => {
                    return { nodeid: value.nodeId } as ExtendedNodeConnData;
                });
                this.filterFrozenNodes();
                this.filterNodesToShow(this.projectInfoService.activeLayer);
                this.preloaderService.hide();
                this.changeDetector.markForCheck();
            });
        }

        this.selectedNodes = [];
        for (const station of this.historyService.logicalTopology.getSelectedStations()) {
            this.selectedNodes.push(station.getName());
        }
    }

    ngOnDestroy() {
        if (this.layersChangedSubscriber) {
            this.layersChangedSubscriber.unsubscribe();
            this.layersChangedSubscriber = null;
        }
    }

    getProjectLayers() {
        if (this.projectInfoService.layersOptions?.length) {
            this.layers = this.projectInfoService.getLayersAsKeyValueObj();
        }
    }

    getNodeLayerIndex(nodeName): number {
        return this.historyService.logicalTopology.getStationByName(nodeName).getLayerIndex();
    }

    nodeSelected(name: string, selected: boolean) {
        const nodeIndex = this.getNodeIndex(this.selectedNodes, name);
        if (nodeIndex === -1 && selected) {
            this.selectedNodes.push(name);
        } else if (nodeIndex !== -1 && !selected) {
            this.selectedNodes.splice(nodeIndex, 1);
        }
        this.changeDetector.markForCheck();
    }

    linkFrozen(fromName: string, toName: string, isForced: boolean) {
        this.changeNodeFrozenInstalled(fromName, toName, isForced);
    }

    linkInstalled(fromName: string, toName: string, isInstalled: boolean) {
        this.changeNodeFrozenInstalled(fromName, toName, undefined, isInstalled);
    }

    closeModal() {
        this.nodesList = undefined;
        this.nodesListOriginal = undefined;
        this.dialogRef.close('cancel');
    }

    trackNodesData(_index, item: NodeConnectedDataDTO) {
        return item.nodeid;
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthReportsTabs[tabIndex];
        this.filterNodesToShow(this.projectInfoService.activeLayer);
    }

    filterNodesToShow(layerIndex = -1) {
        switch (this.currentTab) {
            case GtthReportsTabs.ALL:
                if (layerIndex === -1) {
                    this.nodesList = this.nodesListOriginal;
                } else {
                    this.nodesList = this.nodesListOriginal.filter((node) => {
                        return this.getNodeLayerIndex(node.nodeid) === layerIndex;
                    });
                }
                break;

            case GtthReportsTabs.NEW:
                this.nodesList = this.nodesListOriginal.filter((node) => {
                    return !node.isUserForced && (layerIndex === -1 || this.getNodeLayerIndex(node.nodeid) === layerIndex);
                });
                break;

            case GtthReportsTabs.INSTALLED:
                this.nodesList = this.nodesListOriginal.filter((node) => {
                    return node.isInstalled && (layerIndex === -1 || this.getNodeLayerIndex(node.nodeid) === layerIndex);
                });
                break;
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }

    private getNodeIndex(list: string[], nodeName: string): number {
        return list.findIndex((name: string) => {
            return name === nodeName;
        });
    }

    private changeNodeFrozenInstalled(fromName: string, toName: string, isForced: boolean, isInstalled?: boolean) {
        if (this.nodesList) {
            this.nodesList
                .filter((node) => node.nodeid === fromName || node.nodeid === toName)
                .forEach((node) => {
                    node.isUserForced = isDefined(isForced) ? isForced : node.isUserForced;
                    node.isInstalled = isDefined(isInstalled) ? isInstalled : node.isInstalled;
                });
            this.changeDetector.markForCheck();
        }
    }

    private filterFrozenNodes() {
        this.nodesListOriginal.forEach((nodeData) => {
            const nodeId = this.historyService.logicalTopology.getStationByName(nodeData.nodeid).getId();
            nodeData.isUserForced = false;
            nodeData.isInstalled = false;
            this.historyService.logicalTopology.getAllStationLinks(nodeId).forEach((link) => {
                if (link.isUserForced()) {
                    nodeData.isUserForced = link.isUserForced();
                }
                if (link.isInstalled()) {
                    nodeData.isInstalled = link.isInstalled();
                }
            });
        });
    }
}

@Component({
    selector: 'winde-report-constraint-compliance-max-hops-node-list-popup',
    template: '',
    standalone: false
})
export class GtthReportConstraintComplianceMaxHopsNodesListPopupComponent implements OnInit {
    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }

        const routerPath = this.activatedRoute.routeConfig.path;
        const type = routerPath.split('/').slice(-1)[0];

        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthReportConstraintComplianceNodesListComponent,
            ProjectInteractiveWindowsPopupService.REP_CONSTRAINT_COMPLIANCE_NODES_LIST_MAX_HOPS,
            routerPath,
            bboxParams,
            type
        );
    }
}

@Component({
    selector: 'winde-report-constraint-compliance-max-links-node-list-popup',
    template: '',
    standalone: false
})
export class GtthReportConstraintComplianceMaxLinksNodesListPopupComponent implements OnInit {
    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }

        const routerPath = this.activatedRoute.routeConfig.path;
        const type = routerPath.split('/').slice(-1)[0];

        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthReportConstraintComplianceNodesListComponent,
            ProjectInteractiveWindowsPopupService.REP_CONSTRAINT_COMPLIANCE_NODES_LIST_MAX_LINKS,
            routerPath,
            bboxParams,
            type
        );
    }
}
